import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { ArrowLeft, Phone } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE;

// Full country list with flags and dialing codes
const COUNTRIES = [
  { name: 'United States', code: 'US', dial: '1' },
  { name: 'United Kingdom', code: 'GB', dial: '44' },
  { name: 'Israel', code: 'IL', dial: '972' },
  { name: 'Germany', code: 'DE', dial: '49' },
  { name: 'France', code: 'FR', dial: '33' },
  { name: 'Japan', code: 'JP', dial: '81' }
];

// Prepare options for react-select with small flags
const countryOptions = COUNTRIES.map(c => ({
  value: c.dial,
  label: (
    <div className="flex items-center">
      <img
        src={`https://flagcdn.com/16x12/${c.code.toLowerCase()}.png`}
        alt={c.name}
        className="w-4 h-3 mr-2"
      />
      <span className="text-sm">+{c.dial}</span>
    </div>
  )
}));

const selectStyles = {
  control: base => ({
    ...base,
    background: 'var(--card-bg)',
    borderColor: 'var(--border-color)',
    boxShadow: 'none',
    minHeight: '2.5rem',
    height: '2.5rem',
    minWidth: '90px',
    '&:hover': { borderColor: 'var(--border-color)' }
  }),
  valueContainer: base => ({
    ...base,
    height: '2.5rem',
    padding: '0 0.5rem',
    flexWrap: 'nowrap'
  }),
  singleValue: base => ({
    ...base,
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  input: base => ({ ...base, margin: 0, padding: 0, color: 'var(--text-primary)' }),
  dropdownIndicator: base => ({
    ...base,
    padding: '0 4px',
    color: 'var(--text-primary)'
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: base => ({ ...base, background: 'var(--card-bg)', color: 'var(--text-primary)' }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? 'var(--hover-light)' : 'var(--card-bg)',
    color: 'var(--text-primary)',
    '&:active': { background: 'var(--hover-dark)' }
  })
};

export default function UpdatePhone({ onPhoneUpdated }) {
  const navigate = useNavigate();
  const { regToken } = useParams();
  const location = useLocation();
  const currentPhoneCountry = location.state?.currentPhoneCountry;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [phoneData, setPhoneData] = useState({
    phoneCountry: currentPhoneCountry || countryOptions[0].value,
    phone: ''
  });

  const initialCheckDone = useRef(false);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove all non-digits
    setPhoneData(prev => ({ ...prev, phone: value }));
    setErrors({});
    setGeneralError('');
  };

  const handleSelectChange = option => {
    setPhoneData(prev => ({ ...prev, phoneCountry: option.value }));
    setErrors({});
    setGeneralError('');
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    if (!phoneData.phone) {
      newErrors.Phone = 'Phone number is required';
      isValid = false;
    } else if (!/^\d+$/.test(phoneData.phone) || phoneData.phone.length < 7) {
      newErrors.Phone = 'Invalid phone number';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate() || resendCooldown > 0) return;

    setLoading(true);
    setErrors({});
    setGeneralError('');

    try {
      const res = await fetch(`${API_BASE}/signup/${regToken}/phone`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          PhoneCountry: phoneData.phoneCountry,
          Phone: phoneData.phone
        })
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        // Start the 15-second cooldown
        setResendCooldown(15);
        const interval = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Navigate back to the registration page which will show the verify phone component
        navigate(`/signup/${regToken}`, { replace: true });
        return;
      }
      
      if (res.status === 400) {
        sessionStorage.removeItem('registrationToken');
        navigate('/login', { replace: true });
        return;
      }
      
      if (data.errors) {
        setErrors(data.errors);
      } else if (res.status === 429) {
        setGeneralError('Please wait a few more seconds before updating your phone number again.');
      } else {
        setGeneralError(data.message || 'Failed to update phone number');
      }
    } catch (err) {
      console.error('Failed to update phone:', err);
      setGeneralError('Unable to connect to the server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;

    const checkRegistration = async () => {
      // Skip verification check if we came from PhoneVerification
      if (location.state?.fromPhoneVerification) {
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/signup/${regToken}/phonev`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (res.status === 404) {
          // Registration not found, redirect to signup
          sessionStorage.removeItem('registrationToken');
          navigate('/signup', { replace: true });
          return;
        }

        const data = await res.json();

        // Handle redirect URL if present
        if (data.redirectUrl) {
          if (data.redirectUrl === '/login') {
            sessionStorage.removeItem('registrationToken');
          }
          navigate(data.redirectUrl, { replace: true });
          return;
        }

        if (!res.ok) {
          setGeneralError(data.message || 'Failed to verify registration status');
        }
      } catch (err) {
        console.error('Failed to check registration:', err);
        setGeneralError('Unable to connect to the server');
      }
    };

    checkRegistration();
  }, [regToken, navigate, location.state]);

  return (
    <div>
      <div className="max-w-sm w-full sm:max-w-md p-4 my-8 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-6">
        <div className="text-center space-y-4">
          {/* Phone Handset Icon */}
          <div className="flex justify-center mb-4">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">Update Phone</h2>
        </div>

        {generalError && (
          <div className="p-4 rounded bg-[var(--card-bg)] border border-[var(--border-color)] shadow-sm">
            <div className="flex items-center gap-3">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="var(--accent-primary)"/>
              </svg>
              <span className="text-[var(--text-primary)] text-sm">{generalError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="flex gap-2">
              <div className="shrink-0">
                <Select
                  options={countryOptions}
                  value={countryOptions.find(o => o.value === phoneData.phoneCountry)}
                  onChange={handleSelectChange}
                  isSearchable
                  styles={{
                    ...selectStyles,
                    control: (base) => ({
                      ...selectStyles.control(base),
                      borderColor: errors.Phone ? 'rgb(239, 68, 68)' : 'var(--border-color)',
                      '&:hover': {
                        borderColor: errors.Phone ? 'rgb(239, 68, 68)' : 'var(--border-color)'
                      }
                    })
                  }}
                  isDisabled={loading}
                />
              </div>
              <div className="relative flex-1">
                <input
                  type="text"
                  name="new-phone"
                  id="phone"
                  value={phoneData.phone}
                  onChange={handlePhoneChange}
                  disabled={loading}
                  autoComplete="new-phone"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  data-form-type="other"
                  className={`w-full p-2 pl-10 pr-9 h-10 border rounded bg-[var(--card-bg)] focus:outline-none ${
                    errors.Phone 
                      ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                      : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                  } disabled:opacity-50`}
                  placeholder="Phone number"
                />
                <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              </div>
            </div>
            {errors.Phone && (
              <p className="mt-1 text-red-500 text-sm">{errors.Phone}</p>
            )}
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading || resendCooldown > 0}
              className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] active:bg-[var(--hover-dark)] focus-visible:bg-[var(--hover-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent-primary)]"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-[var(--btn-text-on-accent)] border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending...
                </div>
              ) : resendCooldown > 0 ? (
                'Send Verification Code'
              ) : (
                'Send Verification Code'
              )}
            </button>
          </div>
        </form>

        <div className="flex justify-start">
          <button
            onClick={() => navigate(`/signup/${regToken}`)}
            className="flex items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return
          </button>
        </div>
      </div>
    </div>
  );
} 